import { Button, Stack } from '@mui/material';
import { useNavigate } from 'react-router';
import {
  ResourceCard,
  ResourceCardContainer,
  ResourceCardContent,
  ResourceCardHeader,
  ResourceCardMetaItem,
  ResourceCardPrices,
  ResourceCardTitle,
} from '@/components/shared/resource-card';
import { courseTypeIconNames } from '@/constants/resources/resources-icons';
import type { Course } from '@/models/catalog/course';
import { formatDurationWithModality } from '@/utils/format-duration-with-modality';

const buildProductPageSearchParams = (
  urlParams: Record<string, string>
): string => {
  const productPageSearchParams = new URLSearchParams();

  Object.entries(urlParams).forEach(([key, value]) => {
    productPageSearchParams.set(key, value);
  });

  const decodedParams = decodeURIComponent(productPageSearchParams.toString());
  return decodedParams ? `?${decodedParams}` : '';
};

const buildProductPageLink = ({
  courseId,
  urlParams,
}: {
  courseId: number;
  urlParams: string;
}): string => {
  const productPage = `/cursos/${courseId}`;

  return `${productPage}${urlParams}`;
};

export const CourseCard = ({
  data,
  urlParams,
  onToggle,
}: {
  data: Course;
  urlParams?: Record<string, string>;
  onToggle?: (course: Course) => void;
}) => {
  const navigate = useNavigate();
  const productPageUrlParams = {} as Record<string, string>;

  if (data?.offer_state && data?.offer_city)
    productPageUrlParams.location = `${data?.offer_state},${data?.offer_city}`;
  if (data?.offer_hub) productPageUrlParams.hub = `${data?.offer_hub}`;
  if (data?.offer_period) productPageUrlParams.period = `${data?.offer_period}`;

  const processedUrlParams = buildProductPageSearchParams({
    ...urlParams,
    ...productPageUrlParams,
  });

  const productPageLink = buildProductPageLink({
    courseId: data?.id,
    urlParams: processedUrlParams,
  });

  return (
    <ResourceCard
      onClick={!onToggle ? () => navigate(productPageLink) : undefined}
      href={!onToggle && productPageLink ? productPageLink : undefined}
    >
      <ResourceCardHeader institution={{ ...data?.institution, about: '' }} />
      <ResourceCardContainer>
        <ResourceCardTitle>{data?.course_name}</ResourceCardTitle>
        <ResourceCardContent>
          <ResourceCardMetaItem
            icon={courseTypeIconNames[data?.course_type.type]}
          >
            {data?.course_type.label}
          </ResourceCardMetaItem>
          <ResourceCardMetaItem icon="AccessTime">
            {formatDurationWithModality(
              data?.duration?.quantity,
              data?.duration?.type,
              data?.modalities[0]?.type
            )}
          </ResourceCardMetaItem>
        </ResourceCardContent>
        <ResourceCardPrices
          discountPercentage={data?.discount_percentage}
          installments={data?.installments}
          offersQuantity={data?.offers_quantity}
          originalPrice={data?.original_price}
          priceType={data?.price_type}
          priceWithDiscount={data?.price_with_discount}
        />
        {onToggle && (
          <Stack direction={{ md: 'row' }} gap={2}>
            <Button
              variant="outlined"
              onClick={() => onToggle(data)}
              data-testid={`select-course-${data?.id}`}
              fullWidth
            >
              Selecionar
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.open(productPageLink, '_blank')}
              fullWidth
            >
              Ver detalhes
            </Button>
          </Stack>
        )}
      </ResourceCardContainer>
    </ResourceCard>
  );
};
